---
id: apps-api-node-ts-main-repo-src-services-promotion-doc
title: Promotion
owner: Luc LEMEREZ
status: Inactive
lastReviewed: 2026-09-08
visibility: internal
tags:
  - promotion
  - discount
  - checkout
  - email
---

## Product Overview

Gives a new job-seeker signup a time-limited discount on Rezi Pro, shown as a
countdown in the app and reinforced by a short email sequence. The discount a
user holds at any moment is decided by the server, and the same decision
authorises the coupon at checkout, so what the app shows and what Stripe charges
cannot disagree.

### Primary Workflow
A signup is enrolled and immediately granted an offer with an expiry. While that
expiry is in the future, a top banner counts down and the checkout modal sells
the discounted price. A fixed sequence of emails goes out over the following
days; following a link in one of them puts the user back on a live offer, which
is how someone who ignored the first countdown still lands on something urgent.
Paying ends the promotion for that user permanently — no further discount, no
further email.

### Business Logic
Offers form a ladder, ranked by their quarterly discount, and a user holds
exactly one at a time. A plain visit can grant an offer only once, at the moment
it unlocks, so reloading a page cannot restart a countdown that has run out.
Every later grant comes either from a campaign key in an email link — each key
spendable once per user — or from a lapsed window stepping down to a strictly
worse rung. The bottom rung has no expiry and is always grantable, so resolution
always terminates somewhere.

Eligibility is decided once, at enrollment: RMS and enterprise users are
excluded, and so is anyone who has ever had a subscription. An excluded user
gets no state document at all, which is what "no promotion" means everywhere
else in the system.

### Outcomes
An eligible user sees a discount with a deadline in the banner, the onboarding
step and the checkout modal, and the checkout session is created with the
matching Stripe promotion code. Users who are excluded, or who have paid, see
nothing.

### Out of Scope
The reasoning behind the ladder, the window lengths and the email cadence lives
in `docs/promotion-system-brief.md`; this document covers mechanics only. Coupon
creation in Stripe, SendGrid template authoring, and the A/B ramp are managed
outside this service. Lifetime plans are deliberately not discountable.

## Technical Overview

Two Firestore documents carry everything. `promotion_flows/{flowId}` is
configuration — the offer ladder, the campaign keys, the email sequence and all
user-facing copy — authored by us and readable by clients.
`promotion_state/{uid}` is per-user runtime state, written only by this service;
the client subscribes to it and never writes. Because the config lives in
Firestore rather than in code, discounts, copy, timings and the kill switch all
change without a deploy.

Expiry is computed lazily on read rather than scheduled: nothing happens when a
countdown reaches zero except that the next resolve returns a worse offer.

### Entry Points
`POST /v1/promotion/resolve` (`endpoints.ts`, routed by
`apps/api-gateway/config/templates/cloud_functions.tmpl`) runs the algorithm for
the caller. The client calls it once per session — which is what grants an offer
that has just unlocked, since the passage of time writes nothing — and again
with the `?p=<flowId>.<key>` value from an email link, the only path that may
spend a campaign key.

`enrollUser` (`enroll.ts`) is called from the signup path in
`../stripe/endpoints.ts`, where the eligibility signals exist. `cancelPromotion`
(`cancel.ts`) is called from the Stripe subscription webhook in the same file.
`promotionCodeForCheckout` (`entitlement.ts`) is called by
`../stripe/create-checkout.ts`; it re-resolves rather than reading state raw, so
a checkout arriving just after a window lapsed is charged what the user is
entitled to now. The request body is never consulted for a coupon.
`promotion_drip` (`scheduler.ts`) is the email cron, exported through
`src/functions/index.ts`.

### Key Modules
`resolve.ts` holds the state machine: offer selection, the preempt rule that
lets a better offer override a running clock, the cascade, and the transaction
that writes a grant. `flow.ts` reads and caches the config document for 60
seconds and exposes the ladder ordering. `eligibility.ts` owns the exclusion
predicates and mirrors `libs/features/src/promotion/promotion-eligibility.ts`.
`drip.ts` scans for users whose next email is due, applies the purchase guards
and sends; `types.ts` documents both document shapes.

On the client, `libs/stores/src/promotion.ts` is the read model: it listens to
both documents, calls resolve, and derives the copy, discount and coupon code
each placement renders. The placements are the banner
(`libs/features/src/promotion/banner`, rendered through
`libs/features/src/banners`), the onboarding card
(`apps/app-react/src/features/onboarding/components/onboarding-promotion-card.tsx`)
and the checkout modal header
(`apps/app-react/src/rezi-app/_modal/check-out-modal/check-out-modal-header.tsx`
with `ui/coupon-ticket.tsx`). Countdowns use `libs/ui/src/countdown`; remotely
authored copy renders through `libs/ui/src/inline-markdown`, which is markdown
rather than HTML so config cannot inject markup.

`scripts/publish-promotion-flow.js` is how the config document is written — it
validates the ladder and the campaign keys, supports collapsed timings for
testing, and refuses to write to production without an explicit flag.

### Data and Services
Reads and writes `promotion_flows/{flowId}` and `promotion_state/{uid}`, and
reads `users/{uid}` and `users/{uid}/subscriptions`. Client access is constrained
by `apps/app-react/firestore.rules`: a user may read only their own state, and
listing and writing are denied outright.

Stripe promotion codes named in the config are resolved to coupon ids during
checkout. Emails are published to `send-email-topic` through
`../send-email/send-transaction-email.ts` with a template id taken from the
config, so no template needs to be known at compile time.

### Integrations and Side Effects
Granting an offer writes to `promotion_state`, which pushes a new snapshot to
every open tab. Enrollment happens as a side effect of customer creation at
signup and is never allowed to fail it. The subscription webhook cancels the
promotion, and the drip cron sends email and can self-heal a stale `status` when
it finds a subscription the webhook never recorded. The client emits promotion
analytics through `sendEventTracking`. A campaign key arriving in a URL is
stashed in a cookie by `libs/utils/src/promo-campaign.ts` before the app mounts,
because the sign-in redirect drops the query string.

### Constraints and Risks
Usage maps (`offerUsage`, `dripSent`) must be written as nested objects, never
dotted paths: `set({merge: true})` treats a dotted key as a literal field name,
which silently empties the map and disables the limiter it guards. The drip
queries a single field (`nextDripAt`) so that no composite index is required,
which is why cancelling nulls that field instead of the query filtering on
`status`.

The client must not advance or re-resolve on its own when a countdown hits zero;
the server owns that decision. Promotion UI has to wait for `ABStore` readiness
or variant users flash the control experience. Finally, `types.ts` and
`personalCode` in `drip.ts` are deliberate duplicates of their counterparts in
`libs/stores/src/promotion.ts` — this package compiles with `include: ["src"]`
and cannot import from `libs/` without breaking its entry point — so the two
sides must be kept in step by hand.

## Changelog

- 2026-09-08: Created.
